import React, { useCallback, useEffect, useRef, useState } from 'react'
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';

import { submitAnswer } from '../../../utilities/Answer';
import { v4 as uuid } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { stopVoiceOutput } from '../../../services/shared';
import { newMessage, removeMessage, updateSingleMessage } from '../../../actions';
import { updateAudioInputsState } from '../../../actions/environment.action';

export default function VoiceChat() {
  const [transcript, setTranscript] = useState('');
  const mediaStreamRef = useRef<any>(null);
  const recognitionRef = useRef<any>(null);
  const [mid, setMid] = useState(uuid());
  const [isPaused, setIsPaused] = useState(false);

  // Add restart control refs
  const shouldRestartRef = useRef(false);
  const restartTimeoutRef = useRef<NodeJS.Timeout>();

  const environment = useSelector((state: any) => state?.environment);
  const messages = useSelector((state: any) => state?.messages);
  const activeQuestion = useSelector((state: any) => state?.environment.activeQuestion);

  const isMicOn = environment.audioInput.isMicOn;
  const isListening = environment.audioInput.isListening;

  const dispatch = useDispatch();

  const lastSpeechTime = useRef(Date.now());
  const inactivityTimer = useRef<NodeJS.Timeout>();
  const SILENCE_TIMEOUT = 2000; // 2 seconds of silence before considering inactive

  const restartRecognition = useCallback(() => {
    if (!shouldRestartRef.current || isPaused) return;

    console.log('Restarting speech recognition...');

    // Small delay to prevent rapid restart loops
    restartTimeoutRef.current = setTimeout(() => {
      if (shouldRestartRef.current && recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (error) {
          console.error('Error restarting recognition:', error);
          // If start() fails, try again after a longer delay
          setTimeout(() => {
            if (shouldRestartRef.current) {
              try {
                recognitionRef.current.start();
              } catch (retryError) {
                console.error('Retry failed:', retryError);
              }
            }
          }, 1000);
        }
      }
    }, 100);
  }, [isPaused]);

  useEffect(() => {
    // Setup browser SpeechRecognition
    const SpeechRecognitionClass = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionClass) {
      alert('SpeechRecognition is not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognitionClass();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      console.log('Speech recognition started');
    };

    recognition.onspeechstart = () => {
      console.log('Speech detected!');
    };

    // Add onend handler for auto-restart
    recognition.onend = () => {
      console.log('Speech recognition ended');
      if (shouldRestartRef.current && !isPaused) {
        restartRecognition();
      }
    };


    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';
      let confidence = 0;

      if (isPaused) return;

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          confidence = result[0].confidence;
          interimTranscript += result[0].transcript;
        }
      }

      if (interimTranscript && confidence > 0.7) {
        stopVoiceOutput();
      }

      if (!!finalTranscript.length) {
        stopVoiceOutput();
      }

      setTranscript(finalTranscript);

    };

    recognitionRef.current = recognition;
  }, [isPaused, restartRecognition]);

  const startListening = useCallback(async () => {
    try {
      setIsPaused(false);

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');

      if (audioInputs.length === 0) {
        throw new Error('No microphone found. Please connect a microphone and try again.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      lastSpeechTime.current = Date.now();
      mediaStreamRef.current = stream;

      // Enable auto-restart
      shouldRestartRef.current = true;

      recognitionRef.current?.start();
      dispatch(updateAudioInputsState({
        isMicOn: true,
        isListening: true
      }));

    } catch (error) {
      console.error('Microphone access error:', error);
    }
  }, [dispatch]);

  /**
   * This variable is voice output enabled is set to true 
   * whenever there is an output and audio component. 
   * Hence we have used it as a starting point to turn the Mic on 
   * as soon as the boat has provided an answer.
   */

  useEffect(() => {
    if (environment?.isVoiceOutputEnabled) {
      startListening();
      lastSpeechTime.current = Date.now();
      dispatch(updateAudioInputsState({
        isMicOn: true,
        isListening: true
      }));
      return;
    }

  }, [environment?.isVoiceOutputEnabled, dispatch, startListening])

  /**
   * 1. Reset timer when new speech is detected
   * 2. Clear existing timer
   */
  useEffect(() => {
    if (transcript) {
      lastSpeechTime.current = Date.now();

      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    }
  }, [transcript]);

  const stopListening = useCallback(() => {

    // Disable auto-restart before stopping
    shouldRestartRef.current = false;

    // Clear restart timeout if pending
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }

    recognitionRef.current?.stop();
    mediaStreamRef.current?.getTracks().forEach((track: any) => track.stop());
    dispatch(updateAudioInputsState({
      isMicOn: false,
      isListening: false
    }));
  }, [dispatch]);

  const sendRecording = useCallback(async (mid: string, text: string) => {
    if (!text) return;
    stopListening();
    setTranscript('');
    await submitAnswer(text, '', null, false, '', mid);
    setMid(uuid());
  }, [stopListening]);

  useEffect(() => {
    if (!transcript) return;
    const currentTimer = inactivityTimer.current;

    const checkInactivity = () => {
      const timeSinceLastSpeech = Date.now() - lastSpeechTime.current;
      if ((timeSinceLastSpeech >= SILENCE_TIMEOUT) && !!transcript) {
        sendRecording(mid, transcript);
      }
    };

    // Check user inactivity every second
    const intervalId = setInterval(checkInactivity, 1000);

    return () => {
      clearInterval(intervalId);
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
    };
  });

  const addMessage = (transcript: string) => {
    const exists = messages.find((message: any) => message.mid === mid);
    if (transcript) {
      stopVoiceOutput();
      if (exists) {
        dispatch(updateSingleMessage({
          label: transcript,
          position: 'right',
          type: 'STATEMENT',
          mid: mid,
          questionId: activeQuestion?.id,
          isTemp: true
        }));
      } else {
        dispatch(newMessage({
          label: transcript,
          position: 'right',
          type: 'STATEMENT',
          mid: mid,
          questionId: activeQuestion?.id,
          isTemp: true
        }));
      }
    }
  }

  useEffect(() => {
    addMessage(transcript);
    // eslint-disable-next-line
  }, [transcript])

  /**
   * When user wants to change what has already been spoken
   * 1. We reset the transcript for the package.
   * 2. We remove the message in the store
   * 3. We set a new mid to the message.
   **/
  const removeRecording = () => {
    setTranscript('');
    dispatch(removeMessage({ mid }));
    setMid(uuid());
    setIsPaused(false);
  }


  /**
   * When user clicks on pause icon
   * 1. We set isListening to false
   * 2. An icon to turn on the mic is displayed
   * 3. We turn off listening from the package.
   */
  const pauseListening = () => {
    dispatch(updateAudioInputsState({
      isListening: false
    }));
    setIsPaused(true);
    stopListening();
  }

  /**
 * This hook renders when the componenet is initialised for the first time.
 * 1. Reset any transcript present
 * 2. Stop listening to avoid any memory leakage
 */
  useEffect(() => {
    setTranscript('');
    stopVoiceOutput();
    stopListening();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {
        isMicOn && (
          <div className='voice-controls flex justify-center items-center h-voice'>
            {
              !isListening && (
                <img rel="preload" alt="mic-on" onClick={() => startListening()}
                  className="cursor-pointer mr-2" src={process.env.REACT_APP_CDN + "/voice-paused.svg"}></img>
              )
            }
            {
              isListening && (
                <button className="bg-secondaryBgColor btn relative rounded-full p-bpPadding10 mx-2" onClick={pauseListening}>
                  <PauseIcon fontSize="large" sx={{ color: "#666" }}></PauseIcon>
                </button>
              )
            }
            {
              !!transcript && (
                <img onClick={() => removeRecording()} alt="remove" className=" cursor-pointer" src={process.env.REACT_APP_CDN + "/delete.svg"}></img>
              )
            }
          </div>
        )
      }
      {
        !isMicOn && (
          <div className='voice-input-container flex justify-center items-center h-voice'>
            {
              (environment?.typing || environment?.isVoiceOutputEnabled) &&
              <img onClick={() => stopListening()} alt="mic-disabled" className="mx-4" src={process.env.REACT_APP_CDN + "/mic-disabled.svg"}></img>
            }
            {
              (!environment?.typing && !environment?.isVoiceOutputEnabled) &&
              (
                <>
                  <div className="flex flex-col justify-center items-center" >
                    <button id="speech" className="btn relative rounded-full bg-bpPrimary p-bpPadding10" onClick={() => { startListening(); }}>
                      <MicIcon fontSize="large" sx={{ color: "white" }}></MicIcon>
                    </button>
                    <p className="text-secondaryText my-2 mb-0">Tap to Speak</p>
                  </div>

                </>
              )
            }
          </div >
        )
      }


    </>
  )
}
